// Generic socket-layout engine (M8 Phase A · ASK-08x socket render).
//
// Replaces the per-fixture builders' hardcoded position math (fixtures/*-anchor.js)
// with ONE data-driven engine: given a socket's `layout` descriptor + a product list
// + the product dimensions (from boot.productTypes), it returns the placements. The
// renderer (THREE adapter, Phase A integration) turns each placement into a mesh and
// attaches the SAME proven `userData.interactable` payload (Phase B — handlers are NOT
// rewritten). This module is PURE (no THREE, no DOM) — numbers in, numbers out — so it
// unit-tests headlessly like _lighting-recipe / ws3/_placement-rules.
//
// ─── Socket shape consumed — the RATIFIED DECISION-048 / SLICE-1 emit ───
// Mint emits `scene.semantic.fixtures[*].sockets[]`; each socket:
//   socket = {
//     socketId,                   // "MAIN" for single-socket fixtures
//     anchorName,                 // owning fixture instance (addressing only)
//     position:  {x,y,z},         // WORLD = instanceTransform ∘ socketTRS
//     rotationY: number,          // WORLD facing (radians). ⚠️ CONTRACT REFINEMENT (Client, building
//                                 //   the consumer): the emit must carry the socket's RESOLVED rotation,
//                                 //   not just position — fan/shelf/featured are facing-dependent and
//                                 //   can't orient from position alone. Flagged to Coord 2026-06-27.
//     layout:    one of LAYOUT_ALGORITHMS,   // the algorithm (string) — from node.extras.layout
//     interaction: string,        // consumed in Phase B (handler factory), ignored by the layout math
//     capacity?: number,          // hard max items (else derived from params.footprint / product size)
//     accepts?:  string[],        // productTypes; accepts[0] drives dimension lookup
//     params?: { density?, footprint?:{w,d} },   // layout params: 0..1 tightness · usable extent (m)
//     slots?: Array<{x,y,z,rotationY,rotationX?}>,  // ONLY layout "explicit-slots" (baked sub-slots, e.g. spinner)
//   }
//   productDims = boot.productTypes keyed by type → { dimensions:{w,h,d} }
//
// Placement output (renderer-agnostic): { productIndex, x, y, z, rotationY, rotationX, slot }
//   rotationX = -π/2 means "lay flat" (cover faces world-up); 0 means upright/face-out.

export const LAYOUT_ALGORITHMS = Object.freeze([
	"flat-pile",          // stacked flat on a surface, jittered           (was pile-table)
	"fan-spread",         // 1–3 fan on a surface                          (was fan-table)
	"vertical-stack",     // single neat flat stack, cycle-browse          (was stack-table)
	"featured-hero",      // 1–few upright, face-out, prominent            (was featured-comic)
	"spine-out-row",      // linear row, spine facing out                  (was book-row)
	"face-out-shelf-row", // linear row on a shelf/wall, cover facing out  (was wall-shelf + vinyl-wall-shelf)
	"spinner-arc",        // radial carousel, rows × perRow facing out     (was spinner-rack)
	"browse-bin",         // front-to-back flip bin                        (was vinyl-bin)
	"explicit-slots",     // positions come from baked SLOT_* geometry (geometry-exact alternative to spinner-arc)
]);

const num = (v, d) => (Number.isFinite(v) ? v : d);
const COMIC_FALLBACK = { w: 0.170, h: 0.260, d: 0.0032 };

// Deterministic per-socket jitter (no Math.random — reproducible across reloads/SSR).
function makeJitter(seedStr) {
	let s = 0x811c9dc5;
	for (let i = 0; i < (seedStr || "").length; i++) { s ^= seedStr.charCodeAt(i); s = (s * 0x01000193) >>> 0; }
	return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return (s / 0xffffffff) * 2 - 1; };
}

function dimsFor(socket, productDims) {
	const t = socket?.accepts?.[0] || "comic";
	const d = productDims?.[t]?.dimensions || productDims?.[t] || null;
	return d && Number.isFinite(d.w) ? { w: d.w, h: d.h, d: num(d.d, 0.0032) } : COMIC_FALLBACK;
}

// How many items to actually place: min(stock, layout.capacity, geometric capacity).
function resolveCount(socket, nStock, geomCap) {
	const cap = num(socket?.capacity, Infinity);
	return Math.max(0, Math.min(nStock, cap, geomCap));
}

/**
 * Compute placements for one socket. Pure.
 * @returns {{ algorithm, capacity, rendered, placements: Array, deferred?: boolean }}
 */
export function layoutProducts({ socket, products = [], productDims = {} } = {}) {
	const algo = socket?.layout;   // DECISION-048: layout is the algorithm string
	const p = socket?.position || { x: 0, y: 0, z: 0 };
	const yaw = num(socket?.rotationY, 0);
	const dim = dimsFor(socket, productDims);
	const nStock = products.length;
	const density = Math.min(1, Math.max(0, num(socket?.params?.density, 0.5)));
	const result = (placements, geomCap) => ({
		algorithm: algo, capacity: Math.min(num(socket?.capacity, geomCap), geomCap),
		rendered: placements.length, placements,
	});

	// Facing basis in the XZ plane: rowDir = local +X, faceDir = local +Z, both rotated by yaw.
	const cosY = Math.cos(yaw), sinY = Math.sin(yaw);
	const along = (centre, t) => ({ x: centre.x + t * cosY, z: centre.z + t * sinY });   // step along rowDir
	const forward = (centre, t) => ({ x: centre.x + t * sinY, z: centre.z + t * cosY }); // step along faceDir

	switch (algo) {
		case "explicit-slots": {
			// THE single explicit-slots reader (coord 3c5999d): poses come from the fixture
			// GLB's baked SLOT_* empties, fed in here as `socket.slots` by the stage — the boot
			// socket.slots[] stays absent by design (no duplication). Maps stock → the first N
			// slots in order; passes each slot's full baked pose (`quaternion`) straight through.
			const slots = Array.isArray(socket.slots) ? socket.slots : [];
			const n = resolveCount(socket, nStock, slots.length);
			const placements = [];
			for (let i = 0; i < n; i++) {
				const s = slots[i];
				placements.push({ productIndex: i, x: s.x, y: s.y, z: s.z, rotationY: num(s.rotationY, yaw), rotationX: num(s.rotationX, 0), quaternion: s.quaternion || null, slot: i });
			}
			return result(placements, slots.length);
		}

		case "flat-pile": {
			// Flat stack on the surface, deterministic jitter (was pile-table).
			const gap = dim.d + 0.0008;
			const geomCap = 20;
			const n = resolveCount(socket, nStock, geomCap);
			const j = makeJitter(socket.anchorName || "pile");
			const placements = [];
			for (let i = 0; i < n; i++) {
				placements.push({
					productIndex: i,
					x: p.x + j() * 0.016, y: p.y + i * gap + 0.001, z: p.z + j() * 0.016,
					rotationY: yaw + j() * (15 * Math.PI / 180), rotationX: -Math.PI / 2, slot: i,
				});
			}
			return result(placements, geomCap);
		}

		case "fan-spread": {
			// 1–3 fan, centre-out fill (was fan-table). Side comics angled ±FAN, offset so
			// bottom corners just touch. Faithful to the existing geometry.
			const FAN = 18 * Math.PI / 180;
			const cos = Math.cos(FAN), sin = Math.sin(FAN);
			const offX = dim.w / 2 * (1 + cos) + dim.h / 2 * sin;
			const offZ = -(dim.h / 2 * (1 - cos) + dim.w / 2 * sin);
			const slotAngles = [+FAN, 0, -FAN];
			const slotOffsets = [{ a: -offX, d: offZ }, { a: 0, d: 0 }, { a: +offX, d: offZ }];
			const n = resolveCount(socket, nStock, 3);
			const order = n === 1 ? [1] : n === 2 ? [0, 2] : [0, 1, 2];
			const placements = [];
			for (let i = 0; i < n; i++) {
				const slot = order[i], o = slotOffsets[slot];
				const a = along(p, o.a), f = forward({ x: a.x, z: a.z }, -o.d); // o.d is player-ward (negative faceDir)
				placements.push({ productIndex: i, x: f.x, y: p.y + dim.d / 2 + 0.001, z: f.z, rotationY: yaw + slotAngles[slot], rotationX: -Math.PI / 2, slot });
			}
			return result(placements, 3);
		}

		case "face-out-shelf-row": {
			// Linear pack-from-left along the shelf, cover facing out, slight lean (was
			// wall-shelf / vinyl-wall-shelf). Capacity from footprint; density tightens the gap.
			const WALL_MARGIN = 0.05;
			const usable = Math.max(0, num(socket?.params?.footprint?.w, 0.6) - WALL_MARGIN * 2);
			const gap = 0.004 + (1 - density) * 0.03;                 // density 1 → tight, 0 → airy
			const pitch = dim.w + gap;
			const geomCap = Math.max(0, Math.floor((usable + gap) / pitch));
			const n = resolveCount(socket, nStock, geomCap);
			const lean = -5 * Math.PI / 180;
			const start = -((n - 1) * pitch) / 2;                     // centre the packed run
			const placements = [];
			for (let i = 0; i < n; i++) {
				const a = along(p, start + i * pitch);
				placements.push({ productIndex: i, x: a.x, y: p.y + dim.h / 2, z: a.z, rotationY: yaw, rotationX: lean, slot: i });
			}
			return result(placements, geomCap);
		}

		case "featured-hero": {
			// 1–few upright, face-out, evenly spread, prominent (was featured-comic).
			const geomCap = num(socket?.capacity, 3);
			const n = resolveCount(socket, nStock, geomCap);
			const pitch = dim.w + 0.04;
			const start = -((n - 1) * pitch) / 2;
			const placements = [];
			for (let i = 0; i < n; i++) {
				const a = along(p, start + i * pitch);
				placements.push({ productIndex: i, x: a.x, y: p.y + dim.h / 2, z: a.z, rotationY: yaw, rotationX: 0, slot: i });
			}
			return result(placements, geomCap);
		}

		case "spine-out-row": {
			// Linear pack-from-left along the row, packed by item thickness, upright, spine
			// facing out (renderer maps the spine face). Capacity from footprint (was book-row).
			const gap = 0.003 + (1 - density) * 0.012;
			const pitch = dim.d + gap;
			const usable = Math.max(0, num(socket?.params?.footprint?.w, 0.6) - 0.02);
			const geomCap = Math.max(0, Math.floor((usable + gap) / pitch));
			const n = resolveCount(socket, nStock, geomCap);
			const start = -((n - 1) * pitch) / 2;
			const placements = [];
			for (let i = 0; i < n; i++) {
				const a = along(p, start + i * pitch);
				placements.push({ productIndex: i, x: a.x, y: p.y + dim.h / 2, z: a.z, rotationY: yaw, rotationX: 0, slot: i });
			}
			return result(placements, geomCap);
		}

		case "vertical-stack": {
			// Single neat flat stack rising in Y (was stack-table). Light deterministic jitter
			// so it doesn't read CG-perfect; flat (cover up), cycle-browsed in Phase B.
			const gap = dim.d + 0.0008;
			const geomCap = 24;
			const n = resolveCount(socket, nStock, geomCap);
			const j = makeJitter(socket.anchorName || "stack");
			const placements = [];
			for (let i = 0; i < n; i++) {
				placements.push({
					productIndex: i,
					x: p.x + j() * 0.004, y: p.y + i * gap + 0.001, z: p.z + j() * 0.004,
					rotationY: yaw + j() * (4 * Math.PI / 180), rotationX: -Math.PI / 2, slot: i,
				});
			}
			return result(placements, geomCap);
		}

		case "browse-bin": {
			// Upright, packed front-to-back along faceDir (depth), cover facing out, flip-browsed
			// (was vinyl-bin). Capacity from crate depth; perf-capped like the legacy builder.
			const gap = 0.004 + (1 - density) * 0.01;
			const pitch = dim.d + gap;
			const usable = Math.max(0, num(socket?.params?.footprint?.d, 0.4) - 0.03);
			const geomCap = Math.min(40, Math.max(0, Math.floor(usable / pitch)));
			const n = resolveCount(socket, nStock, geomCap);
			const start = -((n - 1) * pitch) / 2;
			const placements = [];
			for (let i = 0; i < n; i++) {
				const f = forward(p, start + i * pitch);
				placements.push({ productIndex: i, x: f.x, y: p.y + dim.h / 2, z: f.z, rotationY: yaw, rotationX: 0, slot: i });
			}
			return result(placements, geomCap);
		}

		case "spinner-arc": {
			// Radial carousel — perRow items around a vertical cylinder × rows, each facing
			// outward (was spinner-rack; the legacy build read baked SLOT_* — use `explicit-slots`
			// instead when geometry-exact pegs matter). Params: radius / perRow / rows / rowHeight.
			const radius = num(socket?.params?.radius, 0.26);
			const perRow = Math.max(1, Math.round(num(socket?.params?.perRow, 8)));
			const rows = Math.max(1, Math.round(num(socket?.params?.rows, 4)));
			const rowH = num(socket?.params?.rowHeight, dim.h + 0.04);
			const geomCap = perRow * rows;
			const n = resolveCount(socket, nStock, geomCap);
			const placements = [];
			for (let i = 0; i < n; i++) {
				const ring = Math.floor(i / perRow), col = i % perRow;
				const ang = yaw + (col / perRow) * Math.PI * 2;   // ang=yaw → +faceDir
				placements.push({
					productIndex: i,
					x: p.x + radius * Math.sin(ang), y: p.y + dim.h / 2 + ring * rowH, z: p.z + radius * Math.cos(ang),
					rotationY: ang, rotationX: 0, slot: i,
				});
			}
			return result(placements, geomCap);
		}

		default:
			// Unknown algorithm → inert (not a crash) so an unmapped socket can't break the scene.
			return { algorithm: algo || null, capacity: 0, rendered: 0, placements: [], deferred: true };
	}
}
